import debounce from 'lodash.debounce'
import { ValidationSchemaKey, validate } from '../validators/validate'
import { RequiredDetailsValues } from '../components/ApplicationForm/ApplicationFormContext'

export type FieldKey = keyof RequiredDetailsValues
export type FieldBools = Partial<Record<FieldKey, boolean>>
export type FieldFnVoid = Partial<Record<FieldKey, () => void>>

type DebouncedSetShowError = (key: FieldKey) => void

// Creates an object with the same keys as the input and sets each one to false
export const createBoolValues = (inputObj: RequiredDetailsValues): FieldBools => {
  const result: FieldBools = {}

  ;(Object.keys(inputObj) as FieldKey[]).forEach((key) => {
    result[key] = false
  })

  return result
}

// Creates a validation error object by validating each field in the input against its current value
export const createValidationErrors = (
  inputObj: RequiredDetailsValues,
  values: RequiredDetailsValues
): RequiredDetailsValues => {
  const result: RequiredDetailsValues = {}

  ;(Object.keys(inputObj) as FieldKey[]).forEach((key) => {
    result[key] = validate(key as ValidationSchemaKey, values[key] || '')
  })

  return result
}

// Creates a map of debounced functions, one per field, to delay showing validation errors
export const createDebounceMap = (
  inputObj: RequiredDetailsValues,
  debouncedSetShowError: DebouncedSetShowError
): FieldFnVoid => {
  const result: FieldFnVoid = {}

  ;(Object.keys(inputObj) as FieldKey[]).forEach((key) => {
    result[key] = debounce(() => debouncedSetShowError(key), 1500)
  })

  return result
}

// Checks that every required field exists and contains a non-empty value
export const checkAreRequiredFieldsValid = (
  requiredDetails: RequiredDetailsValues | undefined,
  requiredFields?: RequiredDetailsValues
): boolean => {
  if (!requiredFields) return true
  if (!requiredDetails) return false

  return (Object.keys(requiredFields) as FieldKey[]).every((key) => {
    const value = requiredDetails[key]
    return typeof value === 'string' ? value.trim() !== '' : !!value
  })
}

// Returns true when there are no error messages in the array
export const checkErrs = (errors: string[]): boolean => {
  return !errors.some((value) => value.length > 0)
}